import { AthleteSettings, Dow } from '@services/engineService';

/**
 * Is the form different from what the server holds?
 *
 * PRD 7 §5 — the « ce que ça change » warning appears on the **first change**, not on mount, so the
 * screen needs a comparison rather than a touched flag. These mirror `sameDays` / `sameVolumes` in
 * `backend/modules/planEngine/settingsStore.ts`, deliberately: the server decides a save is a no-op
 * with exactly this rule, and two different answers would mean warning about a save that does
 * nothing, or staying silent about one that does.
 */

const normaliseDays = (sportDays: Array<{ dow: Dow; sportId: string }>) =>
  [...sportDays]
    .filter((d, i, all) => all.findIndex((o) => o.dow === d.dow && o.sportId === d.sportId) === i)
    .sort((a, b) => a.dow - b.dow || a.sportId.localeCompare(b.sportId));

export const sameDays = (a: AthleteSettings, b: AthleteSettings): boolean =>
  (a.brickDow ?? null) === (b.brickDow ?? null) &&
  JSON.stringify(normaliseDays(a.sportDays)) === JSON.stringify(normaliseDays(b.sportDays));

export const sameVolumes = (a: Record<string, number>, b: Record<string, number>): boolean => {
  const keys = [...new Set([...Object.keys(a), ...Object.keys(b)])];
  return keys.every((k) => a[k] === b[k]);
};

/** `includeTests` is excluded: it is frozen and never sent, so it can never make a save differ. */
export const settingsChanged = (current: AthleteSettings, stored: AthleteSettings): boolean =>
  !sameDays(current, stored) ||
  !sameVolumes(current.sessionsPerWeek, stored.sessionsPerWeek) ||
  Boolean(current.includePpg) !== Boolean(stored.includePpg);
